import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  RotateCcw,
  Lightbulb,
  Target,
  BookOpen,
  ArrowRight,
  ChevronLeft
} from "lucide-react";
import { toast } from "sonner";
import { CoachLauncher } from "@/components/coaching/CoachLauncher";
import { EnhancedExplanation } from "@/components/practice/EnhancedExplanation";
import { MCQFeedbackButton } from "./MCQFeedbackButton";
import { PeerOptionDistribution } from "./PeerOptionDistribution";
import { HighlightableText } from "./HighlightableText";
import { FellowDifficultyRating } from "./FellowDifficultyRating";
import { MCQDiscussions } from "./MCQDiscussions";
import { PeerExplanations } from "./PeerExplanations";
import { ConfidenceSelector } from "./ConfidenceSelector";

export type MCQ = {
  id: string;
  stem: string;
  options: string[];
  correctIndex: number;
  explanation?: string;
  tags?: string[];
  difficulty?: number;
  stemImageUrl?: string | null;
  explanationImageUrl?: string | null;
};

interface ModernMCQCardProps {
  mcq: MCQ;
  onSubmit?: (selected: number, correct: boolean, confidence?: number | null) => void;
  onClearAnswer?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
  showTimer?: boolean;
  timeLimit?: number; // in seconds
  mode?: "practice" | "exam" | "review";
  /** Controlled: pre-selected answer index (for revisiting answered questions) */
  initialSelectedIndex?: number | null;
  /** Controlled: whether to start in revealed state */
  initialRevealed?: boolean;
}

export const ModernMCQCard = ({ 
  mcq, 
  onSubmit, 
  onClearAnswer,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  showTimer = false, 
  timeLimit = 120,
  mode = "practice",
  initialSelectedIndex = null,
  initialRevealed = false,
}: ModernMCQCardProps) => {
  const [selected, setSelected] = useState<number | null>(initialSelectedIndex);
  const [revealed, setRevealed] = useState(initialRevealed);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isActive, setIsActive] = useState(!initialRevealed);
  const [confidence, setConfidence] = useState<number | null>(null);

  const letters = useMemo(() => ["A", "B", "C", "D", "E"], []);

  // Reset state when the question changes (key prop also remounts, this is a safety net)
  useEffect(() => {
    setSelected(initialSelectedIndex);
    setRevealed(initialRevealed);
    setTimeElapsed(0);
    setIsActive(!initialRevealed);
    setConfidence(null);
  }, [mcq.id, initialSelectedIndex, initialRevealed]);

  // Timer logic
  useEffect(() => {
    if (!showTimer || !isActive || revealed) return;
    
    const interval = setInterval(() => {
      setTimeElapsed(prev => {
        if (prev >= timeLimit) {
          setIsActive(false);
          if (mode === "exam") {
            handleSubmit(true); // Auto-submit in exam mode
          }
          return prev;
        }
        return prev + 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showTimer, isActive, revealed, timeLimit, mode]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (revealed) return;
      
      const num = Number(e.key);
      if (num >= 1 && num <= mcq.options.length) {
        setSelected(num - 1);
      }
      if (e.key === "Enter" && selected !== null) {
        handleSubmit();
      }
      if (e.key === "Escape") {
        reset();
      }
    };
    
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, revealed, mcq.options.length]);

  const handleSubmit = (autoSubmit = false) => {
    if (selected == null && !autoSubmit) return;
    
    const finalSelected = autoSubmit ? selected ?? -1 : selected!;
    const ok = finalSelected === mcq.correctIndex;
    setRevealed(true);
    setIsActive(false);
    onSubmit?.(finalSelected, ok, confidence);
  };

  const reset = () => {
    setSelected(null);
    setRevealed(false);
    setTimeElapsed(0);
    setIsActive(true);
  };

  const isCorrect = revealed && selected === mcq.correctIndex;
  const isIncorrect = revealed && selected !== mcq.correctIndex && selected !== null;
  const timeProgress = showTimer ? (timeElapsed / timeLimit) * 100 : 0;
  const difficultyColor = {
    1: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    2: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    3: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
    4: "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
    5: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
  }[mcq.difficulty ?? 3];

  return (
    <Card className="medical-card animate-fade-in">
      <CardHeader className="space-y-4">
        {/* Header with badges and timer */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={difficultyColor}>
              <Target className="h-3 w-3 mr-1" />
              Level {mcq.difficulty ?? 3}
            </Badge>
            {mcq.tags?.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
          
          {showTimer && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4" />
              <span className={cn(
                "font-mono",
                timeElapsed > timeLimit * 0.8 && "text-destructive"
              )}>
                {Math.max(0, timeLimit - timeElapsed)}s
              </span>
            </div>
          )}
        </div>

        {/* Timer progress bar */}
        {showTimer && (
          <Progress 
            value={timeProgress} 
            className={cn(
              "h-1",
              timeProgress > 80 && "bg-destructive/20 [&>div]:bg-destructive"
            )}
          />
        )}

        {/* Question stem */}
        <div className="space-y-3">
          <HighlightableText
            mcqId={mcq.id}
            text={mcq.stem}
            as="div"
            className="text-lg font-semibold leading-relaxed"
          />
          {mcq.stemImageUrl && (
            <img
              src={mcq.stemImageUrl}
              alt="Question illustration"
              loading="lazy"
              className="rounded-lg border max-h-80 w-auto"
            />
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Answer options */}
        {mcq.options.map((option, i) => {
          const isSelected = selected === i;
          const isCorrectOption = revealed && mcq.correctIndex === i;
          const isWrongSelected = revealed && isSelected && !isCorrectOption;
          
          return (
            <button
              key={i}
              className={cn(
                "w-full rounded-lg border-2 p-4 text-left transition-all duration-200",
                "hover:border-primary/50 hover:bg-muted/50",
                !revealed && "hover:shadow-md",
                isSelected && !revealed && "border-primary bg-primary/5 shadow-md",
                isCorrectOption && "border-accent bg-accent/10 text-foreground",
                isWrongSelected && "border-destructive bg-destructive/10 text-foreground",
                revealed && "cursor-default"
              )}
              onClick={() => !revealed && setSelected(i)}
              disabled={revealed}
            >
              <div className="flex items-start gap-3">
                <span className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full border text-xs font-bold",
                  isSelected && !revealed && "border-primary bg-primary text-primary-foreground",
                  isCorrectOption && "border-accent bg-accent text-accent-foreground",
                  isWrongSelected && "border-destructive bg-destructive text-destructive-foreground",
                  !isSelected && !isCorrectOption && !isWrongSelected && "border-muted-foreground/30"
                )}>
                  {isCorrectOption ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : isWrongSelected ? (
                    <XCircle className="h-4 w-4" />
                  ) : (
                    letters[i]
                  )}
                </span>
                <span className="flex-1 leading-relaxed">{option}</span>
              </div>
            </button>
          );
        })}
      </CardContent>

      <CardFooter className="flex flex-col space-y-4">
        {/* Confidence selector — pre-submission */}
        {!revealed && selected !== null && (
          <div className="w-full">
            <ConfidenceSelector value={confidence} onChange={setConfidence} />
          </div>
        )}

        {/* Action buttons */}
        <div className="flex w-full items-center justify-between">
          <Button 
            variant="outline" 
            onClick={reset}
            className="flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          
          <div className="flex gap-2">
            {revealed && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    reset();
                    onClearAnswer?.();
                    toast.success("Answer cleared. You can re-attempt this question.");
                  }}
                  className="flex items-center gap-2 border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20"
                >
                  <RotateCcw className="h-4 w-4" />
                  Clear Answer
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setRevealed(false)}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Hide Answer
                </Button>
              </>
            )}
            
            <Button 
              onClick={() => handleSubmit()} 
              disabled={selected == null || revealed}
              className="flex items-center gap-2 bg-gradient-medical"
            >
              {revealed ? (
                isCorrect ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Correct!
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    Incorrect
                  </>
                )
              ) : (
                "Submit Answer"
              )}
            </Button>
          </div>
        </div>

        {/* Enhanced Explanation */}
        {revealed && mcq.explanation && (
          <div className="w-full space-y-4 animate-fade-in">
            <EnhancedExplanation
              mcqId={mcq.id}
              question={mcq.stem}
              correctAnswer={mcq.options[mcq.correctIndex]}
              originalExplanation={mcq.explanation}
            />

            {mcq.explanationImageUrl && (
              <img
                src={mcq.explanationImageUrl}
                alt="Explanation illustration"
                loading="lazy"
                className="rounded-lg border max-h-80 w-auto mx-auto"
              />
            )}

            <PeerOptionDistribution
              mcqId={mcq.id}
              optionCount={mcq.options.length}
              correctIndex={mcq.correctIndex}
            />

            <FellowDifficultyRating mcqId={mcq.id} />

            <PeerExplanations mcqId={mcq.id} />

            <MCQDiscussions mcqId={mcq.id} />

            {/* Coaching Button */}
            <div className="w-full flex flex-wrap items-center justify-center gap-3">
              <CoachLauncher
                mcqId={mcq.id}
                topic={mcq.tags?.[0] || 'General Medicine'}
                stem={mcq.stem}
                selectedOption={letters[selected ?? 0]}
                correctOption={letters[mcq.correctIndex]}
                explanation={mcq.explanation}
              />
              {revealed && <MCQFeedbackButton mcqId={mcq.id} mcqStem={mcq.stem} />}
            </div>
          </div>
        )}

        {/* Performance hint */}
        {revealed && !mcq.explanation && (
          <div className="w-full rounded-lg border bg-muted/20 p-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <BookOpen className="h-4 w-4" />
              <span>No explanation available for this question.</span>
            </div>
          </div>
        )}

        {/* Prev / Next CTA row — always visible when navigation handlers exist */}
        {(onPrevious || onNext) && (
          <div className="w-full flex items-center justify-between gap-3 pt-2 border-t">
            <Button
              variant="outline"
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              onClick={onNext}
              disabled={!hasNext}
              size="lg"
              className={cn(
                "flex items-center gap-2",
                revealed && "bg-gradient-medical animate-fade-in"
              )}
            >
              Next Question
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};